/**
 * Thread Status 钩子
 * 1. 接收该账户下 thread 状态变化，更新左侧 agents 列表
 * 2. 侧边收起的时候不实时更新
 * 3. 和当前 thread list 匹配不上的时候刷新列表
 */

import { threadsStateUpdate } from '@/services/thread';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

type IThreadListItem = THREAD.ThreadListItem;

interface useThreadStatusParams {
  threads: IThreadListItem[];
  setThreads: (threads: IThreadListItem[]) => void;
  collapsed: boolean;
  refreshThreads: () => void;
}

const useThreadStatus = ({
  threads,
  setThreads,
  collapsed,
  refreshThreads,
}: useThreadStatusParams) => {
  const pathname = usePathname();
  useEffect(() => {
    const handleThreadStatusChanged = (event: Event) => {
      if (collapsed) {
        return;
      }
      const customEvent = event as CustomEvent;
      const eventData = customEvent?.detail;
      // 处理模板小红点
      if (eventData?.event === 'thread_status') {
        const { state, thread_id, project_id } = eventData;
        const threadIndex = threads.findIndex(
          (thread) =>
            thread.thread_id === thread_id && thread.project_id === project_id,
        );

        if (
          state === 'async_tag_task_completed' ||
          state === 'async_tag_task_failed'
        ) {
          // 判断当前是否在该会话页面
          const isActive = pathname?.includes(thread_id);

          // 如果是当前页面，则标记为已查看
          if (isActive) {
            threadsStateUpdate('completed', thread_id);
          }
        }

        // 其实应该只用监听 completed 就行，full_completed 做个兼容处理吧
        if (state === 'full_completed' || state === 'completed') {
          // FIX: 当会话完成的时候，前端主动刷新列表
          refreshThreads();
        }

        if (threadIndex !== -1) {
          const thread = threads[threadIndex];
          thread.state = state;
          setThreads([...threads]);
        } else {
          refreshThreads();
        }
      }
    };

    // Add event listener
    window.addEventListener(
      'api-notify',
      handleThreadStatusChanged as EventListener,
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        'api-notify',
        handleThreadStatusChanged as EventListener,
      );
    };
  }, [threads, setThreads, collapsed, pathname]);
};

export default useThreadStatus;
